import {
  createAircraftManufacturer,
  verifyAssetManufacturerName,
} from '~/modules/aircraft_manufacturer/data/aircraftManufacturerApi'
import {
  createAircraftModel,
  verifyAssetModelName,
} from '~/modules/aircraft_model/data/aircraftModelApi'
import { generateNSizeArray } from '~/utils/generateNSizeArray'
import { IAircraft, IAircraftModel, ICompanyAsset, IRealStateAsset } from '~/utils/types'

const DELIMITER = '<:!:>'
export function splitFromString(value: string, parts: number, delimiter = DELIMITER): string[] {
  const result = value.split(delimiter)

  const lengthDiff = result.length - parts

  if (lengthDiff === 0) return result

  if (lengthDiff > 0) {
    return result.slice(0, parts - 1)
  } else {
    result.push(...generateNSizeArray(Math.abs(lengthDiff)).map(() => ''))
  }

  return result
}

export function assetToCompanyAsset(values: IAircraft): ICompanyAsset {
  const msn = values.serial_number ?? ''
  const assetModel = values.aircraft_model?.name ?? ''
  const manufacturer = values.aircraft_model?.manufacturer?.name ?? ''

  const translatedModel = splitFromString(assetModel, 3)

  return {
    department_area: msn,
    company_plant: translatedModel.at(0) ?? '',
    address_line: translatedModel.at(1) ?? '',
    zip: translatedModel.at(2) ?? '',
    company_name: manufacturer,
  }
}

export function assetToRealStateAsset(values: IAircraft): IRealStateAsset {
  const assetModel = values.aircraft_model?.name ?? ''
  const manufacturer = values.aircraft_model?.manufacturer?.name ?? ''
  const msn = values.serial_number ?? ''

  const translatedManufacturer = splitFromString(manufacturer, 2)
  const translatedMsn = msn
  const translatedModel = splitFromString(assetModel, 3)

  return {
    city: translatedManufacturer.at(0) ?? '',
    state: translatedManufacturer.at(1) ?? '',
    extra_fields: {
      type: values.extra_fields?.type ?? '',
    },
    property_name: translatedModel.at(0) ?? '',
    suite_number: translatedMsn,
    address_line: translatedModel.at(1) ?? '',
    zip: translatedModel.at(2) ?? '',
    id_external_system: values.id_external_system ?? '',
  }
}

export function joinCompanyAssetTranslations(values: ICompanyAsset, delimiter = DELIMITER) {
  return {
    serial_number: values.department_area,
    assetModelName: [values.company_plant, values.address_line, values.zip].join(delimiter),
    manufacturerName: values.company_name,
  }
}

export function joinRealStateAssetTranslations(values: IRealStateAsset, delimiter = DELIMITER) {
  return {
    assetModelName: [values.property_name, values.address_line, values.zip].join(delimiter),
    manufacturerName: [values.city, values.state].join(delimiter),
    serial_number: values.suite_number,
    extra_fields: values.extra_fields,
    id_external_system: values.id_external_system,
  }
}

export function formatRealStateAddress(msn: string, model: string, _manufacturer: string): string {
  const translatedMsn = msn
  const translatedModel = splitFromString(model, 3)

  return `Suite ${translatedMsn} - ${translatedModel.at(0)}`
}

export async function findOrCreateCustomAssetModel(
  identifier: string,
  manufacturer_id: string,
  asset_type_id?: string,
): Promise<string | null> {
  asset_type_id ??= '683d2d83945dad8ddbe6c287' // Company Asset Type ID (static)

  let result: string | null = null

  const existingModelRes = await verifyAssetModelName({
    name: identifier,
    manufacturer_id,
  })

  if (existingModelRes.available) {
    const response = await createAircraftModel({
      name: identifier,
      manufacturer: manufacturer_id,
      asset_type: asset_type_id,
    } as unknown as IAircraftModel)

    result = response._id ?? null
  } else {
    result = existingModelRes.existing_id
  }

  return result
}

export async function findOrCreateCustomAssetManufacturer(
  identifier: string,
): Promise<string | null> {
  let result: string | null = null

  const existingManufacturerRes = await verifyAssetManufacturerName({
    name: identifier,
  })

  if (existingManufacturerRes.available) {
    const response = await createAircraftManufacturer({
      aliases: [],
      name: identifier,
      shortname: '',
    })

    result = response._id ?? null
  } else {
    result = existingManufacturerRes.existing_id
  }

  return result
}
